import { SORT_ORDER } from '../../globals/constants';
import { LanguageCodes } from '../../util/language';
import { fetchAndFormat, makeUrlObject } from '../../util/util';

/**
 * @typedef {Object} VanityPath
 * @property {string} route
 * @property {string} to
 */

/**
 * Fetches all vanity urls for the site regardless of language.
 * Unlike most other v2 apis, this modifies the source object.
 * English comes first, then French.
 * @returns {Promise<{internalVanities: VanityPath[], externalVanities: VanityPath[]}>}
 */
export async function getVanityURLs(languageCode) {
	const params = new URLSearchParams({
		"include": "field_vanity_target_link,field_vanity_target_link.field_internal_link",
		"fields[node--program]": "path",
		"fields[node--simple_page]": "path",
		"fields[node--complex_page]": "path",
	});

	const urlEN = makeUrlObject('jsonapi/node/vanity_url', LanguageCodes.En);
	urlEN.search = params.toString();
	
	const urlFR = makeUrlObject('jsonapi/node/vanity_url', LanguageCodes.Fr);
	urlFR.search = params.toString();

	const allVanities = [...await fetchAndFormat(urlEN), ...await fetchAndFormat(urlFR)]
  // Sort order is guaranteed after a filter, so sort, then we can filter by external and internal after
  const sortedVanities = allVanities.sort((a) => a.language === languageCode ? SORT_ORDER.LESS_THAN : SORT_ORDER.GREATHER_THAN);

	const internalVanities = sortedVanities.filter(i => i.field_vanity_target_link?.field_internal_link?.path?.alias)
  .map(i => {
    const fixedRoute = i.field_vanity_url.startsWith('/') ? i.field_vanity_url : '/' + i.field_vanity_url;
    return {
      route: fixedRoute.trim(),
      to: i.field_vanity_target_link?.field_internal_link?.path?.alias,
    }
  });

	const externalVanities = sortedVanities.filter(i => i.field_vanity_target_link?.field_external_link?.uri)
  .map(i => {
    const fixedRoute = i.field_vanity_url.startsWith('/') ? i.field_vanity_url : '/' + i.field_vanity_url;
    return {
      route: fixedRoute.trim(),
      to: i.field_vanity_target_link?.field_external_link?.uri,
    }
  });

	return { internalVanities, externalVanities };
}

export async function getVanityUrlByProgramId(programId, language) {
  const params = new URLSearchParams({
		"include": "field_vanity_target_link,field_vanity_target_link.field_internal_link",
		"fields[node--program]": "path",
		"fields[node--simple_page]": "path",
		"fields[node--complex_page]": "path",
	});

	const url = makeUrlObject('jsonapi/node/vanity_url', language);
	url.search = params;

	const resp = await fetchAndFormat(url);
  // TODO: Ideally you should be able to filter this in the API call. I could not figure this out.
  const selectedProgram = resp.find(i => i.field_vanity_target_link?.field_internal_link?.id === programId)

	return selectedProgram;
}